import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { Row, Col, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { difference, union } from 'lodash';
import { confirmAlert } from 'react-confirm-alert';

//components
import withLocalization from '../../../hoc/withLocalization';
import GenericList from '../../../components/GenericList/GenericList';
import GenericFilter from '../../../components/GenericList/GenericFilter';
import Button from '../../../components/CustomButton/CustomButton';
import Checkbox from '../../../components/CustomCheckbox/CustomCheckbox';

//elements
import LoadingSpinner from '../../../elements/LoadingSpinner';

//assets
import Delete from '../../../assets/img/deleting.svg';

const member_assignment_statuses = [
    { code: 'assigned', name: 'Assigned' },
    { code: 'unassigned', name: 'Unassigned' },
];

class TaskMemberAssignForm extends Component {
    constructor(props) {
        super(props);
        this.state = {
            filters: { name: '', status: '' },
            appliedFilters: { name: '', status: '' },
            selectedMemberIds: [],
            currentPageUnassignedIds: [],
            currentPageMembers: [],
            assignedCount: 0,
            unassignedCount: 0,
            loading: false,
            reloadKey: Date.now(),
            initialCountLoaded: false,
            page: 0,
            isPageAddedOrEdited: false
        };
    }

    setFilter = (field, value) => {
        this.setState(prev => ({
            filters: { ...prev.filters, [field]: value }
        }));
    };

    handlePageChange = (page, isPageAddedOrEdited) => {
        this.setState({ page, isPageAddedOrEdited });
    };

    onFilter = () => {
        this.setState(prev => ({
            appliedFilters: { ...prev.filters },
            reloadKey: Date.now(),
            selectedMemberIds: [],
            currentPageUnassignedIds: [],
            currentPageMembers: [],
            page: 0,
            isPageAddedOrEdited: false
        }));
    };

    updateStatusFilter = (status) => {
        const { appliedFilters } = this.state;
        const newStatus = appliedFilters.status === status ? '' : status;

        this.setState(prev => ({
            filters: { ...prev.filters, status: newStatus }
        }), this.onFilter);
    };

    componentDidMount() {
        this.loadCounts();
    }

    loadCounts = async () => {
        const { taskStore, projectId, taskId, userStore } = this.props;
        const { currentUser } = userStore;
        if (!projectId || !taskId) return;
        let pmMembers = '';
        if (currentUser.user_type !== 'admin' && currentUser?.Manages?.length) {
            pmMembers = currentUser.Manages.join(',');
        }
        try {
            const { assigned, unassigned } = await taskStore.getTaskMembersFromProjectCount(projectId, taskId, pmMembers);
            this.setState({
                assignedCount: assigned || 0,
                unassignedCount: unassigned || 0,
                initialCountLoaded: true
            });
            const total = (assigned || 0) + (unassigned || 0);
            if(this.props.updateTaskMemberCount){
                this.props.updateTaskMemberCount(assigned || 0, total);
            }
        } catch (err) {
        }
    };

    toggleMemberSelection = (id) => {
        this.setState(prev => {
            const { selectedMemberIds } = prev;
            const newIds = selectedMemberIds.includes(id)
                ? selectedMemberIds.filter(i => i !== id)
                : [...selectedMemberIds, id];
            return { selectedMemberIds: newIds };
        });
    };

    toggleSelectAll = () => {
        this.setState(prev => {
            const { currentPageUnassignedIds, selectedMemberIds } = prev;
            const allSelected = currentPageUnassignedIds.length > 0 && currentPageUnassignedIds.every(id => selectedMemberIds.includes(id));
            if (allSelected) {
                return { selectedMemberIds: difference(selectedMemberIds, currentPageUnassignedIds) };
            } else {
                return { selectedMemberIds: union(selectedMemberIds, currentPageUnassignedIds) };
            }
        });
    };

    fetchMembers = (params = {}) => {
        const { name, status } = this.state.appliedFilters;
        const { projectId, taskId, taskStore, userStore } = this.props;
        const { currentUser } = userStore;
        let pmMembers = '';
        if (currentUser.user_type !== 'admin' && currentUser?.Manages?.length) {
            pmMembers = currentUser.Manages.join(',');
        }
        const options = {
            name: name || '',
            assigned: status || 'all',
            page: params.page || 0,
            pagesize: params.pagesize,
            isNotPagination: params.isNotPagination || false,
            pmMembers: pmMembers,
        };
        return taskStore.getTaskMembersFromProjectMembers(projectId, taskId, options).then(response => {
            const list = response.rows || [];
            const unassignedIds = list.filter(m => !m.is_assigned_to_task).map(m => m.user_id);
            this.setState({ currentPageUnassignedIds: unassignedIds, currentPageMembers: list });
            return response;
        });
    };

    assignSelectedMembers = async () => {
        const { taskStore, commonStore, t, taskId } = this.props;
        const { selectedMemberIds } = this.state;
        if (!selectedMemberIds.length) return;
        this.setState({ loading: true });
        try {
            await taskStore.saveTaskMembers({ taskId, members: selectedMemberIds.map(id => ({ user_id : id })) });
            commonStore.addNotification(t('Saved'), null, 'success');
            this.setState({
                reloadKey: Date.now(),
                selectedMemberIds: [],
                currentPageUnassignedIds: [],
                currentPageMembers: [],
                loading: false,
                isPageAddedOrEdited: true
            }, () => this.loadCounts());
        } catch (err) {
            this.setState({ loading: false });
            commonStore.addNotification(err.message || t('Error assigning members'), null, 'error');
        }
    };

    unassignMember = async (userId) => {
        const { taskStore, commonStore, t, taskId } = this.props;
        const row = this.state.currentPageMembers.find(r => r.user_id === userId);
        if (row && row.is_assignee) {
            return commonStore.addNotification(t('Cannot remove Assignee'), null, 'warning');
        }
        try {
            confirmAlert({
                title: `${t('Confirm')} ?`,
                message: t('Are you sure you want to remove this member from the task?'),
                buttons: [
                    {
                        label: t('Yes'),
                        onClick: async () => {
                            await taskStore.deleteTaskMember(userId, taskId);
                            commonStore.addNotification(t('Saved'), null, 'success');
                            this.setState({ reloadKey: Date.now(), isPageAddedOrEdited: true }, () => this.loadCounts());
                        },
                    },
                    {
                        label: t('No'),
                        onClick: () => { }
                    },
                ]
            })
        } catch (err) {
            commonStore.addNotification(err.message || t('Error unassigning member'), null, 'error');
        }
    };

    renderCheckboxCell = (row) => {
        const userId = row.original.user_id;
        const isAssigned = !!row.original.is_assigned_to_task;
        const isAssignee = !!row.original.is_assignee;
        const { selectedMemberIds } = this.state;
        const isSelected = selectedMemberIds.includes(userId);
        const { t, userStore } = this.props;
        const { currentUser } = userStore;
        const isPm = currentUser.user_type === 'pm';
        const isDisabled = isPm && (isAssignee || userId === currentUser.id);

        return (
            <div className="d-flex align-items-center justify-content-center">
                {isAssigned ? (
                    <OverlayTrigger
                        key="unassign_member"
                        placement="top"
                        overlay={<Tooltip id="tooltip-top">
                            {isDisabled ? t("You don't have enough rights to perform this action") : t('Unassign')}
                        </Tooltip>}
                    >
                        <Button icon_sm fill disabled={isDisabled} onClick={() => this.unassignMember(userId)}>
                            <img src={Delete} alt="delete" />
                        </Button>
                    </OverlayTrigger>
                ) : (
                    <Checkbox
                        inlineBlock
                        isChecked={isSelected}
                        onChange={() => this.toggleMemberSelection(userId)}
                        wrapperClassName="checkbox-position-fix"
                        labelClassName="ps-0"
                    />
                )}
            </div>
        );
    };

    render() {
        const { projectId, taskId, t } = this.props;
        const { filters, appliedFilters, loading, selectedMemberIds, currentPageUnassignedIds, assignedCount, unassignedCount, initialCountLoaded } = this.state;
        if (!projectId || !taskId) return <div />;
        if (loading) return <LoadingSpinner />;

        const totalMembers = (assignedCount || 0) + (unassignedCount || 0);
        const hasActiveFilters = !!appliedFilters.name || !!appliedFilters.status;

        if (initialCountLoaded && totalMembers === 0 && !hasActiveFilters && !loading) {
            return (
                <div className="text-center mt-5">
                    <i className="fa fa-users fa-3x text-muted mb-3" aria-hidden="true"></i>
                    <h6 className="text-muted" style={{ textTransform: 'none' }}>{t('No project members found.')} {t('Please assign members to the project before adding task members.')}</h6>
                </div>
            );
        }

        const isAllSelected = currentPageUnassignedIds.length > 0 &&
            currentPageUnassignedIds.every(id => selectedMemberIds.includes(id));

        const columns = [
            {
                Header: t('Members'),
                id: 'fullname',
                textAlign: 'start',
                accessor: 'fullname',
                isSortable: true,
                Cell: ({ row }) => <div className="text-start">{row.original.fullname}</div>
            },
            {
                Header: appliedFilters.status === 'assigned' ? t('Unassign') : appliedFilters.status === 'unassigned' ? t('Assign') : `${t('Assign')} / ${t('Unassign')}`,
                id: 'assigned',
                textAlign: 'center',
                accessor: 'user_id',
                Cell: (cellProps) => this.renderCheckboxCell(cellProps.row),
            }
        ];

        return (
            <div>
                <div className='mb-3'>
                    <GenericList
                        key={this.state.reloadKey}
                        columns={columns}
                        stickyHeader
                        header={
                            <>
                                <Row className="align-items-center gx-2">
                                    <Col xs={12} lg={8} xl={9}>
                                        <Row className="member_status__summary">
                                            <Col xs="auto" className="mt-2 mt-sm-0 d-flex justify-content-center justify-content-sm-start">
                                                <div
                                                    className="highlightedgreen" role="button"
                                                    onClick={() => this.updateStatusFilter('assigned')}
                                                    style={{
                                                        cursor: 'pointer',
                                                        opacity: appliedFilters.status === 'assigned' ? 0.4 : 1
                                                    }}
                                                >
                                                    {`${t('Assigned')} ${t('Members')}`} : {assignedCount}
                                                </div>
                                            </Col>
                                            <Col xs="auto" className="mt-2 mt-sm-0 d-flex justify-content-center">
                                                <div
                                                    className="highlightedred" role="button"
                                                    onClick={() => this.updateStatusFilter('unassigned')}
                                                    style={{
                                                        cursor: 'pointer',
                                                        opacity: appliedFilters.status === 'unassigned' ? 0.4 : 1
                                                    }}
                                                >
                                                    {`${t('Unassigned')} ${t('Members')}`} : {unassignedCount}
                                                </div>
                                            </Col>
                                            <Col xs="auto" className="mt-2 mt-sm-0 d-flex justify-content-center justify-content-sm-end">
                                                <Checkbox
                                                    inlineBlock
                                                    isChecked={isAllSelected}
                                                    onChange={this.toggleSelectAll}
                                                    disabled={currentPageUnassignedIds.length === 0}
                                                    label={t("Select All")}
                                                />
                                            </Col>
                                        </Row>
                                    </Col>

                                    <Col xs="auto" className="d-flex justify-content-center justify-content-lg-end mt-2 mt-lg-0">
                                        {selectedMemberIds.length > 0 && (
                                            <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-assign">{t('Assign')}</Tooltip>}>
                                                <Button onClick={this.assignSelectedMembers} title={t('Assign Selected')} fill>
                                                    {t('Assign Selected')} ({selectedMemberIds.length})
                                                </Button>
                                            </OverlayTrigger>
                                        )}
                                    </Col>
                                </Row>
                                <GenericFilter
                                    filters={filters}
                                    setFilter={(name, value) => this.setFilter(name, value)}
                                    onFilter={() => this.onFilter()}
                                    loading={loading}
                                    hasStatusFilter={true}
                                    statusOptions={member_assignment_statuses}
                                    searchFilterPlaceholder={`${t('Search')} ${t('Members')}`}
                                />
                            </>
                        }
                        requestData={(params) => this.fetchMembers(params)}
                        filters={this.state.appliedFilters}
                        page={this.state.page}
                        isPageAddedOrEdited={this.state.isPageAddedOrEdited}
                        handlePageChange={this.handlePageChange}
                        showPageSizeSelector
                        pageSizeListId="task_members"
                    />
                </div>
            </div>
        );
    }
}

export default inject('userStore', 'commonStore', 'taskStore')(withLocalization(observer(TaskMemberAssignForm)));